import React from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Hotel as HotelIcon,
  Attractions as AttractionsIcon,
  Restaurant as RestaurantIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { THEME_COLORS, THEME_SHADOWS } from '../constants/theme';

const placeCategory = (place) => {
  if (place.category === 'restaurant' || place.types?.includes('restaurant')) return 'restaurant';
  if (place.category === 'hotel' || place.types?.includes('lodging')) return 'hotel';
  return 'attraction';
};

const CATEGORY_ICON = {
  restaurant: <RestaurantIcon sx={{ fontSize: 18, color: THEME_COLORS.categories.restaurant.main }} />,
  hotel: <HotelIcon sx={{ fontSize: 18, color: THEME_COLORS.categories.hotel.main }} />,
  attraction: <AttractionsIcon sx={{ fontSize: 18, color: THEME_COLORS.primary.main }} />,
};

/**
 * The day's route stops with every place picked for each one, so people can
 * see what they've added while browsing suggestions and take things back out.
 */
const RoadTripStopsPanel = ({
  dayLabel,
  stops,
  onRemovePlace,
  onEditStops,
  onSave,
  saving = false,
  saveLabel,
  hasUnsavedChanges = false,
  t,
}) => {
  const placeCount = stops.reduce((total, stop) => total + stop.places.length, 0);

  return (
    <Card
      data-testid="road-trip-stops-panel"
      sx={{ borderRadius: 3, boxShadow: THEME_SHADOWS.light, p: 2 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>
          {t('roadTrip.yourStops')}
        </Typography>
        <Chip label={t('roadTrip.placesCount', { count: placeCount })} size="small" variant="outlined" />
      </Box>
      {dayLabel && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          {dayLabel}
        </Typography>
      )}

      <Box component="ol" sx={{ listStyle: 'none', p: 0, m: 0 }}>
        {stops.map((stop, index) => (
          <Box component="li" key={stop.id} sx={{ position: 'relative', pl: 4, pb: 1.5 }}>
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                width: 22,
                height: 22,
                borderRadius: '50%',
                bgcolor: index === 0 ? THEME_COLORS.categories.transport.main : THEME_COLORS.primary.main,
                color: '#fff',
                fontSize: 12,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {index + 1}
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: '22px', overflowWrap: 'anywhere' }}>
              {stop.name}
            </Typography>

            {stop.places.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                {t('roadTrip.noPlacesYet')}
              </Typography>
            ) : (
              <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mt: 0.5 }}>
                {stop.places.map((place) => (
                  <Box
                    component="li"
                    key={place.placeId || place.name}
                    data-testid="road-trip-panel-place"
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}
                  >
                    {CATEGORY_ICON[placeCategory(place)]}
                    <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap title={place.address || place.name}>
                      {place.name}
                    </Typography>
                    <Tooltip title={t('roadTrip.removePlace', { name: place.name })}>
                      <IconButton
                        size="small"
                        aria-label={t('roadTrip.removePlace', { name: place.name })}
                        onClick={() => onRemovePlace(stop.id, place.placeId)}
                        sx={{ p: 0.5 }}
                      >
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        ))}
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        {t('roadTrip.yourStopsHint')}
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={onEditStops}
          sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600 }}
        >
          {t('roadTrip.editRouteStops')}
        </Button>
        <Button
          size="small"
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={onSave}
          disabled={saving}
          sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600, ml: 'auto' }}
        >
          {saveLabel}
        </Button>
      </Box>
      {hasUnsavedChanges && (
        <Typography
          variant="caption"
          role="status"
          sx={{ display: 'block', mt: 1, color: THEME_COLORS.primary.main, fontWeight: 600 }}
        >
          {t('roadTrip.unsavedPlaces')}
        </Typography>
      )}
    </Card>
  );
};

export default RoadTripStopsPanel;
